import type { Beneficiario } from "./api";
import { roundPercentage } from "./percentage-utils";

export interface BeneficiarioLocal
  extends Omit<
    Beneficiario,
    "beneficiarioId" | "solicitudPolizaId" | "estadoId"
  > {
  id: string;
  numeroPoliza: number;
  porcentaje: number;
  rut: number;
  replicatedFrom?: number;
  hasBeenModified?: boolean;
}

export interface ApiBeneficiarioModification {
  id: number;
  numeroPoliza: number;
  tipoBeneficiario: number;
  rut: number;
  dv: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  fechaNacimiento: string;
  parentesco: number;
  email: string;
  region: number;
  ciudad: number;
  comuna: number;
  direccion: string;
  casaDepto: string;
  celular: string;
  porcentaje: number;
  sexo: number;
  replicatedFrom?: number;
  hasBeenModified?: boolean;
}

export interface ApiBeneficiarioDeletion {
  id: number;
  numeroPoliza: number;
  tipoBeneficiario?: number;
  rut: number;
  dv?: string;
  nombre?: string;
  apellidoPaterno?: string;
  apellidoMaterno?: string;
  fechaNacimiento?: string;
  parentesco?: number;
  email?: string;
  region?: number;
  ciudad?: number;
  comuna?: number;
  direccion?: string;
  casaDepto?: string;
  celular?: string;
  porcentaje?: number;
  sexo?: number;
  replicatedFrom?: number;
}

export interface BeneficiariosStorage {
  added: BeneficiarioLocal[];
  modified: ApiBeneficiarioModification[];
  deleted: ApiBeneficiarioDeletion[];
}

function getStorage(): BeneficiariosStorage {
  if (typeof window === "undefined")
    return { added: [], modified: [], deleted: [] };

  const stored = localStorage.getItem("beneficiarios");
  if (!stored) return { added: [], modified: [], deleted: [] };

  try {
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) {
      return { added: parsed, modified: [], deleted: [] };
    }
    return parsed;
  } catch {
    return { added: [], modified: [], deleted: [] };
  }
}

function saveStorage(storage: BeneficiariosStorage): void {
  if (typeof window === "undefined") return;
  localStorage.setItem("beneficiarios", JSON.stringify(storage));
}

export function saveBeneficiario(beneficiario: BeneficiarioLocal): void {
  const storage = getStorage();
  storage.added.push(beneficiario);
  saveStorage(storage);
}

export function saveModifiedApiBeneficiario(
  beneficiario: ApiBeneficiarioModification
): void {
  const storage = getStorage();
  storage.deleted = storage.deleted.filter((d) => d.id !== beneficiario.id);
  const existingIndex = storage.modified.findIndex(
    (m) => m.id === beneficiario.id
  );
  if (existingIndex >= 0) {
    storage.modified[existingIndex] = beneficiario;
  } else {
    storage.modified.push(beneficiario);
  }
  saveStorage(storage);
}

export function markApiBeneficiarioAsModified(
  beneficiario: ApiBeneficiarioModification
): void {
  saveModifiedApiBeneficiario(beneficiario);
}

export function markApiBeneficiarioForDeletion(
  deletion: ApiBeneficiarioDeletion
): void {
  const storage = getStorage();
  storage.modified = storage.modified.filter((m) => m.id !== deletion.id);
  if (!storage.deleted.some((d) => d.id === deletion.id)) {
    storage.deleted.push(deletion);
  }
  saveStorage(storage);
}

export function unmarkApiBeneficiarioFromDeletion(id: number): void {
  const storage = getStorage();
  storage.deleted = storage.deleted.filter((d) => d.id !== id);
  saveStorage(storage);
}

export function isApiBeneficiarioMarkedForDeletion(id: number): boolean {
  const storage = getStorage();
  return storage.deleted.some((d) => d.id === id);
}

export function getModifiedApiBeneficiario(
  id: number
): ApiBeneficiarioModification | undefined {
  const storage = getStorage();
  return storage.modified.find((m) => m.id === id);
}

export function getBeneficiariosByPoliza(
  numeroPoliza: number
): BeneficiarioLocal[] {
  const storage = getStorage();
  return storage.added.filter((b) => b.numeroPoliza === numeroPoliza);
}

export function getAllBeneficiarios(): BeneficiarioLocal[] {
  const storage = getStorage();
  return storage.added;
}

export function getAllBeneficiariosStorage(): BeneficiariosStorage {
  return getStorage();
}

export function deleteBeneficiario(id: string): void {
  const storage = getStorage();
  storage.added = storage.added.filter((b) => b.id !== id);
  saveStorage(storage);
}

export function updateBeneficiario(
  id: string,
  updates: Partial<BeneficiarioLocal>
): void {
  const storage = getStorage();
  const index = storage.added.findIndex((b) => b.id === id);
  if (index >= 0) {
    storage.added[index] = { ...storage.added[index], ...updates };
    saveStorage(storage);
  }
}

export function clearBeneficiarios(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem("beneficiarios");
}

export function getBeneficiariosGroupedByPoliza(): Record<
  number,
  BeneficiarioLocal[]
> {
  const storage = getStorage();
  const grouped: Record<number, BeneficiarioLocal[]> = {};

  for (const beneficiario of storage.added) {
    if (!grouped[beneficiario.numeroPoliza]) {
      grouped[beneficiario.numeroPoliza] = [];
    }
    grouped[beneficiario.numeroPoliza]!.push(beneficiario);
  }

  return grouped;
}

export function getTotalPercentageByPoliza(
  numeroPoliza: number,
  apiBeneficiarios?: Array<{ id: number; porcentaje: number }>,
  excludeApiBeneficiarioId?: number
): number {
  const storage = getStorage();

  const addedTotal = storage.added
    .filter((b) => b.numeroPoliza === numeroPoliza)
    .reduce((total, b) => total + b.porcentaje, 0);

  const modifiedTotal = storage.modified
    .filter(
      (b) =>
        b.numeroPoliza === numeroPoliza && b.id !== excludeApiBeneficiarioId
    )
    .reduce((total, b) => total + b.porcentaje, 0);

  let apiTotal = 0;
  if (apiBeneficiarios) {
    const modifiedIds = new Set(storage.modified.map((m) => m.id));
    const deletedIds = new Set(storage.deleted.map((d) => d.id));

    apiTotal = apiBeneficiarios
      .filter(
        (b) =>
          !modifiedIds.has(b.id) &&
          !deletedIds.has(b.id) &&
          b.id !== excludeApiBeneficiarioId
      )
      .reduce((total, b) => total + b.porcentaje, 0);
  }

  return roundPercentage(addedTotal + modifiedTotal + apiTotal);
}

export function hasAnyChanges(): boolean {
  const storage = getStorage();
  return (
    storage.added.length > 0 ||
    storage.modified.length > 0 ||
    storage.deleted.length > 0
  );
}

export function getActionsForPoliza(numeroPoliza: number): {
  added: BeneficiarioLocal[];
  modified: ApiBeneficiarioModification[];
  deleted: ApiBeneficiarioDeletion[];
} {
  const storage = getStorage();
  return {
    added: storage.added.filter((b) => b.numeroPoliza === numeroPoliza),
    modified: storage.modified.filter((b) => b.numeroPoliza === numeroPoliza),
    deleted: storage.deleted.filter((b) => b.numeroPoliza === numeroPoliza),
  };
}

function applyActionsToBeneficiarios(
  apiBeneficiarios: Array<{ id: number; rut: number; porcentaje: number }>,
  actions: any
): Array<any> {
  // Aplicar eliminaciones primero
  let beneficiarios = apiBeneficiarios.filter(
    (b) => !actions.deleted.some((d: any) => d.id === b.id)
  );

  // Aplicar modificaciones
  beneficiarios = beneficiarios.map((b) => {
    const modification = actions.modified.find((m: any) => m.id === b.id);
    return modification ? { ...b, ...modification } : b;
  });

  // Agregar nuevos
  beneficiarios = [...beneficiarios, ...actions.added];

  return beneficiarios;
}

export function replicateActionsToPolizas(
  sourcePoliza: number,
  targetPolizas: number[],
  apiBeneficiariosByPoliza: Record<
    number,
    Array<{ id: number; rut: number; porcentaje: number }>
  >
): void {
  const storage = getStorage();
  const sourceActions = getActionsForPoliza(sourcePoliza);

  const filteredTargetPolizas = targetPolizas.filter(
    (poliza) => poliza !== sourcePoliza
  );

  const sourceApiBeneficiarios = apiBeneficiariosByPoliza[sourcePoliza] || [];
  const sourceCurrentState = applyActionsToBeneficiarios(
    sourceApiBeneficiarios,
    sourceActions
  );

  for (const targetPoliza of filteredTargetPolizas) {
    const targetApiBeneficiarios = apiBeneficiariosByPoliza[targetPoliza] || [];

    if (targetApiBeneficiarios.length === 0) {
      const existingReplicatedBeneficiarios = storage.added.filter(
        (b) =>
          b.numeroPoliza === targetPoliza && b.replicatedFrom === sourcePoliza
      );

      if (existingReplicatedBeneficiarios.length === 0) {
        for (const beneficiario of sourceCurrentState) {
          // Verificar por RUT si ya existe en esta póliza (podría ser agregado manualmente)
          const existsByRut = storage.added.some(
            (b) => b.numeroPoliza === targetPoliza && b.rut === beneficiario.rut
          );

          if (!existsByRut) {
            const newId = `${Date.now()}-${Math.random()}-replicated-${targetPoliza}`;
            storage.added.push({
              ...beneficiario,
              id: newId,
              numeroPoliza: targetPoliza,
              replicatedFrom: sourcePoliza,
            });
          }
        }
      }
    }
    // ESTRATEGIA 2: Si la póliza destino SÍ tiene beneficiarios, replicar por RUT
    else {
      // Replicar AGREGADOS de la fuente
      for (const added of sourceActions.added) {
        // Verificar si ya existe un beneficiario con el mismo RUT en el target
        const existsInTarget = targetApiBeneficiarios.some(
          (b) => b.rut === added.rut
        );

        if (!existsInTarget) {
          const newId = `${Date.now()}-${Math.random()}-replicated-${targetPoliza}`;
          storage.added.push({
            ...added,
            id: newId,
            numeroPoliza: targetPoliza,
            replicatedFrom: sourcePoliza,
          });
        }
      }

      // Replicar MODIFICACIONES
      for (const modified of sourceActions.modified) {
        // Buscar beneficiario en target por RUT
        const targetBeneficiario = targetApiBeneficiarios.find(
          (b) => b.rut === modified.rut
        );

        if (targetBeneficiario) {
          const modifiedCopy = {
            ...modified,
            id: targetBeneficiario.id, // Usar ID del target
            numeroPoliza: targetPoliza,
            replicatedFrom: sourcePoliza,
          };
          storage.modified.push(modifiedCopy);
        }
      }

      // Replicar ELIMINADOS
      for (const deleted of sourceActions.deleted) {
        const targetBeneficiario = targetApiBeneficiarios.find(
          (b) => b.rut === deleted.rut
        );

        if (targetBeneficiario) {
          storage.deleted.push({
            ...deleted,
            id: targetBeneficiario.id, // Usar ID del target
            numeroPoliza: targetPoliza,
            replicatedFrom: sourcePoliza,
          });
        }
      }
    }
  }

  saveStorage(storage);
}

export function removeReplicatedActions(sourcePoliza: number): void {
  const storage = getStorage();

  // 1. En added: eliminar solo los que fueron replicados desde esta fuente
  storage.added = storage.added.filter((beneficiario) => {
    const shouldKeep = beneficiario.replicatedFrom !== sourcePoliza;
    if (!shouldKeep) {
    }
    return shouldKeep;
  });

  // 2. En modified: revertir solo los que fueron replicados desde esta fuente
  storage.modified = storage.modified.filter((modificacion) => {
    const shouldKeep = modificacion.replicatedFrom !== sourcePoliza;
    if (!shouldKeep) {
    }
    return shouldKeep;
  });

  // 3. En deleted: revertir solo las eliminaciones replicadas desde esta fuente
  storage.deleted = storage.deleted.filter((eliminacion) => {
    const shouldKeep = eliminacion.replicatedFrom !== sourcePoliza;
    if (!shouldKeep) {
    }
    return shouldKeep;
  });
  saveStorage(storage);
}

export function rutExistsInPoliza(
  numeroPoliza: number,
  rut: number,
  excludeId?: string | number,
  apiBeneficiarios?: Array<{ id: number; rut: number }>
): boolean {
  const storage = getStorage();

  const existsInAdded = storage.added.some(
    (b) =>
      b.numeroPoliza === numeroPoliza && b.rut === rut && b.id !== excludeId
  );

  const existsInModified = storage.modified.some(
    (b) =>
      b.numeroPoliza === numeroPoliza && b.rut === rut && b.id !== excludeId
  );

  let existsInApi = false;
  if (apiBeneficiarios) {
    const deletedIds = new Set(storage.deleted.map((d) => d.id));
    existsInApi = apiBeneficiarios.some(
      (b) => b.rut === rut && !deletedIds.has(b.id) && b.id !== excludeId
    );
  }

  return existsInAdded || existsInModified || existsInApi;
}

export const getAllBeneficiariosParaPoliza = (poliza: any): any[] => {
  const apiBeneficiarios = poliza.beneficiarios || []; // Datos originales de API
  const numeroPoliza = poliza.numeroPoliza;

  // Obtener cambios del store de beneficiarios
  const storage = getStorage();
  const beneficiariosStore = storage;

  // 1. Aplicar MODIFICACIONES a beneficiarios existentes
  const beneficiariosModificados = apiBeneficiarios.map((beneficiario: any) => {
    const modificacion = beneficiariosStore.modified.find(
      (m: any) => m.id === beneficiario.id && m.numeroPoliza === numeroPoliza
    );
    return modificacion ? { ...beneficiario, ...modificacion } : beneficiario;
  });

  // 2. Agregar NUEVOS beneficiarios
  const agregados = beneficiariosStore.added.filter(
    (a: any) => a.numeroPoliza === numeroPoliza
  );

  // 3. Filtrar ELIMINADOS
  const eliminadosIds = new Set(
    beneficiariosStore.deleted
      .filter((d: any) => d.numeroPoliza === numeroPoliza)
      .map((d: any) => d.id)
  );

  const todosBeneficiarios = [...beneficiariosModificados, ...agregados];
  const beneficiariosFinales = todosBeneficiarios.filter(
    (b: any) => !eliminadosIds.has(b.id)
  );

  return beneficiariosFinales;
};

export const updateAddedBeneficiario = (
  beneficiarioId: string,
  updatedData: any
) => {
  const storage = getStorage();
  const addedIndex = storage.added.findIndex((b) => b.id === beneficiarioId);

  if (addedIndex !== -1) {
    // Actualizar el beneficiario
    storage.added[addedIndex] = {
      ...storage.added[addedIndex],
      ...updatedData,
      hasBeenModified: true,
    };

    saveStorage(storage);
  }
};
